/** @format */

import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import RateInterface from '../../../interfaces/billing/RateInterface';
import RateRangesCustomerSummaricInterface, {
    RATE_RANGES_CUSTOMER_SUMMARIC_INITIAL,
} from '../../../interfaces/billing/RateRangesCustomerSummaricInterface';
import { useDispatch } from 'react-redux';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CheckBoxSimple from '../../../components/Form/CheckBoxSimple';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import EditButton from '../../../components/Buttons/EditButton';
import RemoveButton from '../../../components/Buttons/RemoveButton';
import CustomInputExtNumber from '../../../components/CustomInput/CustomInputExtNumber';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import InputFormRest from '../../../components/Form/InputFormRest';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import Loader from '../../../components/Loaders/Loader';

interface RateRangesProps {
    rate: RateInterface;
    updateRateRanges: (rateRanges: RateRangesCustomerSummaricInterface[]) => void;
    rateRanges: RateRangesCustomerSummaricInterface[] | undefined;
    usesRateRanges: boolean;
    updateUsesRateRanges: (usesRateRanges: boolean) => void;
    updateRateRangesWereLoaded: (rateRangesWereLoaded: boolean) => void;
}

export const RateRanges = (props: RateRangesProps): React.ReactElement => {
    const rateRanges = props.rateRanges;
    const [rateRangesLoaded, setRateRangesLoaded] = useState<boolean>(false);
    const updateRateRanges = props.updateRateRanges;
    const usesRateRanges = props.usesRateRanges;
    const updateUsesRateRanges = props.updateUsesRateRanges;
    const updateRateRangesWereLoaded = props.updateRateRangesWereLoaded;
    const rate = props.rate;

    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const [selectedEditIndex, setSelectedEditIndex] = useState<number | null>(null);
    const [inputData, setInputData] = useState<RateRangesCustomerSummaricInterface>(({
        ...RATE_RANGES_CUSTOMER_SUMMARIC_INITIAL,
    } as unknown) as RateRangesCustomerSummaricInterface);

    useEffect((): void => {
        if (!rate.id || rate.id === -1) {
            setRateRangesLoaded(true);
            updateRateRangesWereLoaded(true);
            return;
        }

        if (rateRanges) {
            setRateRangesLoaded(true);
            updateRateRangesWereLoaded(true);
            return;
        }

        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/rates_ranges_customer_summarics`, configDefault.PLATFORM_API_PREFIX)),
            'RATE_GET_LIST',
            [{ name: 'rateId', value: rate.id }],
            [],
            {
                onSuccess: (data: any) => {
                    setRateRangesLoaded(true);
                    updateRateRangesWereLoaded(true);
                    const tmpRateRanges = [];
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        for (let i = 0; i < data['hydra:member'].length; i++) {
                            tmpRateRanges.push(data['hydra:member'][i]);
                        }
                        updateRateRanges(tmpRateRanges);
                        if (tmpRateRanges.length > 0) {
                            updateUsesRateRanges(true);
                        }
                        setRateRangesLoaded(true);
                        updateRateRangesWereLoaded(true);
                    }
                },
                onError: () => {
                    updateRateRanges([]);
                    setRateRangesLoaded(true);
                    updateRateRangesWereLoaded(true);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [rate.id, rateRanges, setRateRangesLoaded, updateUsesRateRanges, updateRateRanges, dispatch, updateRateRangesWereLoaded]);

    const changeInputData = useCallback(
        (e: any, stateName: string): void => {
            if (!stateName || stateName === undefined) {
                stateName = e.target.name;
            }

            if (stateName === 'interval') {
                const numbers = /^[0-9]+$/;
                if (!e.target.value.match(numbers) && e.target.value !== '') {
                    return;
                }
            } else {
                const numbers = /^[0-9.]+$/;
                if (!e.target.value.match(numbers) && e.target.value !== '') {
                    return;
                }
            }
            let tmpRate: Record<string, any> = { ...inputData } as Record<string, any>;
            tmpRate[stateName] = e.target.value;
            setInputData((tmpRate as unknown) as RateRangesCustomerSummaricInterface);
        },
        [inputData, setInputData]
    );

    const onBlurHandler = useCallback(
        (e: any): void => {
            const parsedValue = CurrencyHelper.numericToStandardPln(e.target.value, 10, true, '.');
            setInputData({ ...inputData, [e.target.name]: parsedValue });
            e.target.value = parsedValue;
        },
        [setInputData, inputData]
    );

    const addRange = useCallback((): void => {
        let tmpRateRanges: RateRangesCustomerSummaricInterface[] = [];
        if (rateRanges) {
            tmpRateRanges = [...rateRanges];
        }
        tmpRateRanges.push({ ...inputData });
        updateRateRanges(tmpRateRanges);
        setInputData(({ ...RATE_RANGES_CUSTOMER_SUMMARIC_INITIAL } as unknown) as RateRangesCustomerSummaricInterface);
    }, [rateRanges, inputData, setInputData, updateRateRanges]);

    const saveEdit = useCallback((): void => {
        if (selectedEditIndex === null) {
            return;
        }
        let tmpRateRanges: RateRangesCustomerSummaricInterface[] = [];
        if (rateRanges) {
            tmpRateRanges = [...rateRanges];
        }
        tmpRateRanges[selectedEditIndex] = { ...inputData };
        updateRateRanges(tmpRateRanges);
        setInputData(({ ...RATE_RANGES_CUSTOMER_SUMMARIC_INITIAL } as unknown) as RateRangesCustomerSummaricInterface);
        setSelectedEditIndex(null);
    }, [rateRanges, selectedEditIndex, inputData, updateRateRanges, setInputData, setSelectedEditIndex]);

    const removeRange = useCallback(
        (params: { index: number }): void => {
            let tmpRateRanges: RateRangesCustomerSummaricInterface[] = [];
            if (rateRanges) {
                tmpRateRanges = [...rateRanges];
            }
            tmpRateRanges.splice(params.index, 1);
            updateRateRanges(JSON.parse(JSON.stringify(tmpRateRanges)) as RateRangesCustomerSummaricInterface[]);
        },
        [rateRanges, updateRateRanges]
    );

    const editRange = useCallback(
        (params: { index: number }): void => {
            if (!rateRanges) {
                return;
            }
            setSelectedEditIndex(params.index);
            setInputData(({ ...rateRanges[params.index] } as unknown) as RateRangesCustomerSummaricInterface);
        },
        [rateRanges, setInputData]
    );

    const cancelEdit = useCallback((): void => {
        setSelectedEditIndex(null);
        setInputData(({ ...RATE_RANGES_CUSTOMER_SUMMARIC_INITIAL } as unknown) as RateRangesCustomerSummaricInterface);
    }, [setSelectedEditIndex, setInputData]);

    return (
        <>
            <Loader visible={!rateRangesLoaded} />
            <GridContainer>
                <GridItem xs={12} style={{ padding: 0 }}>
                    <CheckBoxSimple
                        checked={usesRateRanges}
                        label={'pricelists:price_threshold.active'}
                        callback={(): void => {
                            updateUsesRateRanges(!usesRateRanges);
                        }}
                    />
                </GridItem>
                {usesRateRanges && (
                    <>
                        <GridItem xs={12} spacing={0}>
                            <GridContainer>
                                <GridItem xs={6}>
                                    <CustomInputExtNumber
                                        onBlurHandler={onBlurHandler}
                                        onChange={changeInputData}
                                        name="initial"
                                        labelText="pricelists:price_threshold.initial"
                                        required={true}
                                        value={inputData.initial}
                                        decimalLimit={10}
                                    />
                                </GridItem>
                                <GridItem xs={6}>
                                    <CustomInputExtNumber
                                        onBlurHandler={onBlurHandler}
                                        onChange={changeInputData}
                                        name="price"
                                        labelText="pricelists:price_threshold.price"
                                        required={true}
                                        value={inputData.price}
                                        decimalLimit={10}
                                    />
                                </GridItem>
                                <GridItem xs={6}>
                                    <CustomInputExtNumber
                                        onBlurHandler={onBlurHandler}
                                        onChange={changeInputData}
                                        name="threshold"
                                        labelText="pricelists:price_threshold.threshold"
                                        required={true}
                                        value={inputData.threshold}
                                        decimalLimit={10}
                                    />
                                </GridItem>
                                <GridItem xs={6}>
                                    <InputFormRest
                                        stateName={'interval'}
                                        displayFieldName={'pricelists:price_threshold.interval'}
                                        value={inputData.interval}
                                        typeCallback={FormGeneralHelper.TYPE_NUMBER}
                                        typeField={FormGeneralHelper.TYPE_NUMBER}
                                        callback={changeInputData}
                                        required={true}
                                        vMin={1}
                                        vMax={999999}
                                    />
                                </GridItem>
                                <GridItem xs={12}>
                                    {selectedEditIndex === null ? (
                                        <CustomTextButtom
                                            size={'sm'}
                                            callback={addRange}
                                            callbackParam={{}}
                                            label="pricelists:price_threshold.add_threshold"
                                        />
                                    ) : (
                                        <GridContainer>
                                            <CustomTextButtom
                                                size={'sm'}
                                                callback={saveEdit}
                                                callbackParam={{}}
                                                label="pricelists:price_threshold.save_edit"
                                            />
                                            <CustomTextButtom
                                                size={'sm'}
                                                callback={cancelEdit}
                                                callbackParam={{}}
                                                label="pricelists:price_threshold.cancel"
                                            />
                                        </GridContainer>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem xs={12} spacing={0}>
                            {rateRanges && rateRanges.length > 0 && (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" style={{ width: 40 }}>
                                                <TableHeaderContentCell classes={tableStickyHeader} textAlign="center" title="clients:c.commands" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TableHeaderContentCell
                                                    classes={tableStickyHeader}
                                                    textAlign="center"
                                                    title="pricelists:price_threshold.initial"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TableHeaderContentCell
                                                    classes={tableStickyHeader}
                                                    textAlign="center"
                                                    title="pricelists:price_threshold.price"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TableHeaderContentCell
                                                    classes={tableStickyHeader}
                                                    textAlign="center"
                                                    title="pricelists:price_threshold.threshold"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <TableHeaderContentCell
                                                    classes={tableStickyHeader}
                                                    textAlign="center"
                                                    title="pricelists:price_threshold.interval"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rateRanges.map((el: RateRangesCustomerSummaricInterface, index) => {
                                            return (
                                                <TableRow key={`price_threshold_${index}`}>
                                                    <TableCell classes={tableCellRoot}>
                                                        <EditButton
                                                            callback={editRange}
                                                            callbackParam={{ index: index }}
                                                            tooltipTitle={'pricelists:price_threshold.edit'}
                                                        />
                                                        <RemoveButton
                                                            callback={removeRange}
                                                            callbackParam={{ index: index }}
                                                            tooltipTitle={'pricelists:price_threshold.remove'}
                                                        />
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="center">
                                                        {el.initial}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="center">
                                                        {el.price}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="center">
                                                        {el.threshold}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="center">
                                                        {el.interval}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </>
    );
};
